<template>
  <div v-if="element" class="mb-3">

    <HeaderRecordTitleHint
      v-if="element.recordType === 'description'"
      :record="element"
      :showUntitledLabel=false
    />

    <RecordTitleHint
      v-else
      :record="element"
      :showUntitledLabel=false
      @duplicateRecord="duplicateDatasheetRecord"
    />

    <div v-if="element.recordType === 'textarea'">
      <b-form-textarea :id="element['@id']" v-model="element.defaultValue" class="my-1" rows="2"
                       v-validate=element.validatorRules :data-vv-name="element['@id'] || 'name'"
                       :data-vv-as="element.recordType"
                       :required="element.requiredField"/>
      <label v-if="errors.first(element['@id'])" class="text-danger">
        {{ errors.first(element['@id']) }}
      </label>
    </div>

    <div v-else-if="element.recordType === 'text'">
      <b-form-input :id="element['@id']" v-model="element.defaultValue" class="my-1" v-validate=element.validatorRules
                    :data-vv-name="element['@id'] || 'name'" :data-vv-as="element.recordType"
                    :required="element.requiredField"/>
      <label v-if="errors.first(element['@id'])" class="text-danger">
        {{ errors.first(element['@id']) }}
      </label>
    </div>

    <div v-else-if="element.recordType === 'number'">
      <div class="input-group w-75">
        <input type="number" :no-wheel="true" v-model="element.defaultValue" class="form-control" v-validate=element.validatorRules
               :data-vv-name="element['@id'] || 'name'" :data-vv-as="element.recordType"
               :required="element.requiredField"/>
        <div class="input-group-append">
          <button class="btn px-4 btn-outline-secondary" type="button" @click="increment('decrease')">
            <i class="fa fa-minus"></i>
          </button>
          <button class="btn px-4 btn-secondary" type="button" @click="increment('increase')">
            <i class="fa fa-plus"></i>
          </button>
        </div>
      </div>
      <label v-if="errors.first(element['@id'])" class="text-danger">
        {{ errors.first(element["@id"]) }}
      </label>
    </div>

    <div v-else-if="element.recordType === 'dropdown'">
      <b-form-select :id="element['@id']" v-model="element.optionValue" :options="element.optionsSet.optionsValues"
                     text-field="value" value-field="@id" v-validate=element.validatorRules
                     :data-vv-name="element['@id'] || 'name'"
                     data-vv-as="dropdown - single selection" :required="element.requiredField">
        <template #first>
          <b-form-select-option :value="null" disabled>
            - Select an option -
          </b-form-select-option>
        </template>
      </b-form-select>
      <label v-if="errors.first(element['@id'])" class="text-danger">
        {{ errors.first(element["@id"]) }}
      </label>
    </div>

    <div v-else-if="element.recordType === 'multiple_choice'">
      <v-select class="p-0 m-0" label="- Select one or more -" v-model="element.optionValue"
                :items="element.optionsSet.optionsValues" item-text="value" item-value="@id"
                multiple single-line outline
                v-validate=element.validatorRules :data-vv-name="element['@id'] || 'name'"
                data-vv-as="dropdown - multiple selection" :required="element.requiredField"
                @input="$validator.validate(element['@id'] || 'name')"/>
      <label v-if="errors.first(element['@id'])" class="text-danger">
        {{ errors.first(element["@id"]) }}
      </label>
    </div>

    <div v-else-if="element.recordType === 'datetime'">
      <date-picker placeholder="YYYY/MM/DD h:mm:ss A" :config="datetimePickerOptions" :wrap="true"
                   v-model="element.defaultValue" v-validate=element.validatorRules
                   :data-vv-name="element['@id'] || 'name'"
                   :data-vv-as="element.recordType" :required="element.requiredField"/>
      <label v-if="errors.first(element['@id'])" class="text-danger">
        {{ errors.first(element["@id"]) }}
      </label>
    </div>

    <div v-else-if="element.recordType === 'radio'">
      <div class="form-group mb-0">
        <b-form-radio-group
          :id="element['@id']"
          v-model="element.optionValue"
          :options="element.optionsSet.optionsValues"
          :required="element.requiredField"
          v-validate=element.validatorRules
          :data-vv-name="element['@id']"
          :data-vv-as="element.recordType"
          text-field="value"
          value-field="@id"
          stacked
        ></b-form-radio-group>
        <label v-if="errors.first(element['@id'])" class="text-danger">
          {{ errors.first(element["@id"]) }}
        </label>
      </div>
    </div>

    <div v-else-if="element.recordType === 'image'">
      <RecordImages :project-id="projectId"
                    :record="element" v-model="element.files"
                    :preview="false" v-validate=element.validatorRules
                    :data-vv-name="element['@id'] || 'name'" :data-vv-as="element.recordType"
                    :required="element.requiredField">
      </RecordImages>
      <label v-if="errors.first(element['@id'])" class="text-danger">
        {{ errors.first(element["@id"]) }}
      </label>
    </div>

  </div>
</template>

<script type="text/javascript">
import DatePicker from 'vue-bootstrap-datetimepicker'
import 'pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css'

// Using font-awesome 5 icons
import '@fortawesome/fontawesome-free/css/fontawesome.css'
import '@fortawesome/fontawesome-free/css/regular.css'
import '@fortawesome/fontawesome-free/css/solid.css'

import RecordImages from './RecordImages.vue'
import PrivateTooltip from '../common/PrivateTooltip.vue'
import RecordTitleHint from './RecordTitleHint.vue'
import HeaderRecordTitleHint from "./HeaderRecordTitleHint.vue";

// Using font-awesome 5 icons
$.extend(true, $.fn.datetimepicker.defaults, {
  icons: {
    time: 'far fa-clock',
    date: 'far fa-calendar',
    up: 'fas fa-arrow-up',
    down: 'fas fa-arrow-down',
    previous: 'fas fa-chevron-left',
    next: 'fas fa-chevron-right',
    today: 'fas fa-calendar-check',
    clear: 'far fa-trash-alt',
    close: 'far fa-times-circle'
  }
})

const DATETIME_PICKER_OPTIONS = {
  allowInputToggle: true,
  format: "YYYY/MM/DD h:mm:ss A",
  showClear: true,
  showClose: true,
  useStrict: true,
}

const UNTITLED_RECORD = "Untitled Question"

export default {
  inject: ['parentValidator'],

  components: {
    DatePicker,
    RecordImages,
    PrivateTooltip,
    RecordTitleHint,
    HeaderRecordTitleHint,
  },

  props: {
    projectId: {
      type: String,
      required: true,
    },
    element: {
      type: Object,
      default: () => {
      }
    },

    recordType: {
      type: String,
      default: ''
    },

    duplicateDatasheetRecord: {
      type: Function,
      default: () => {
      }
    },

    removeDatasheetRecordModalIsOpen: {
      type: Boolean,
      default: false
    },

    showDefaultValue: {
      type: Boolean,
      default: false
    },

    uploadLoadingStatus: {
      type: Boolean,
      default: false
    },

    page: {
      type: String,
      default: ""
    }
  },

  data() {
    return {
      removeRecordModalIsOpen: this.removeDatasheetRecordModalIsOpen,
      datetimePickerOptions: DATETIME_PICKER_OPTIONS,
      uploadLoadingStatusValue: this.uploadLoadingStatus,
    }
  },

  created() {
    this.$validator = this.parentValidator
  },

  methods: {
    increment(direction) {
      const oldString = this.element.defaultValue;
      let oldNum = 0
      const isUndefined = (oldString === undefined)
      const isNanString = (oldString === 'NaN')
      const isNotANumber = isNaN(oldString)

      if (!isUndefined && !isNanString && !isNotANumber) {
        oldNum = Number(oldString)
      }
      let newValue = 0;
      if (direction === 'increase') {
        newValue = Number(oldNum) + 1;
      } else if (direction === 'decrease') {
        newValue = Number(oldNum) - 1;
      }
      // check to see if value is in bounds
      const maxObj = this.element.validators.find((validator) => {
        return validator.validatorType === "max"
      })
      const minObj = this.element.validators.find((validator) => {
        return validator.validatorType === "min"
      })
      if (maxObj && maxObj.value && Number(maxObj.value) < newValue) {
        newValue = Number(maxObj.value);
      }
      if (minObj && minObj.value && Number(minObj.value) > newValue) {
        newValue = Number(minObj.value);
      }
      this.element.defaultValue = newValue.toString();
    },
  },
  computed: {
    modalIsOpen: {
      get: function () {
        return this.removeRecordModalIsOpen
      },
      set: function (newValue) {
        this.removeRecordModalIsOpen = newValue

        this.$emit('modalIsOpen', this.modalIsOpen)
      }
    },

    uploadLoadingStatusVal: {
      get: function () {
        return this.uploadLoadingStatusValue
      },
      set: function (newValue) {
        this.uploadLoadingStatusValue = newValue

        this.$emit('uploadLoadingStatus', this.uploadLoadingStatusVal)
      }
    }
  }
}

</script>

<style lang="scss" scoped>
#custom-vuetify-style {
  .datasheet-organisms-order-number {
    top: 14px;
    position: absolute;
    left: -41px;
    background: #eee;
    border: 1px solid #DCE0E6;
    width: auto;
    min-width: 30px;
    padding: 4px 0;
    text-align: center;
    font-size: 12px;
  }

  .datasheet-record-options {
    top: -10px;
    position: absolute;
    right: -41px;
    background: #95DAF6;
    border: 1px solid #DCE0E6;
    width: auto;
    min-width: 30px;
    padding: 4px 0;
    text-align: center;

    .datasheet-record-option:hover {
      color: #C0C99C !important;
    }

    .v-icon {
      margin-bottom: 10px;
    }

    .fa-clone {
      color: #ffffff;
      font-size: 16px;
    }
  }

  .datasheet-record-option-value-preview-side ::v-deep {
    .v-label {
      color: rgba(0, 0, 0, .54) !important;
    }

    .v-input__control {
      width: 100%;

      .v-input__slot {
        margin: 0;

        .v-input--radio-group__input {
          > .v-label {
            border-bottom: 1px solid #DCE0E6;
            font-size: 15px;
          }

          .option-value-preview-side {
            border-bottom: 1px solid #DCE0E6;
            margin: 0 0 0 35px;
            padding: 7px 16px 0 0;
            position: relative;

            &:last-child {
              border: none;
            }
          }
        }

        .v-input--selection-controls__input {
          position: absolute;
          top: 7px;
          right: 4px;

          .v-icon,
          .v-icon.accent--text {
            font-size: 20px;
            color: rgb(135, 152, 63) !important;
            caret-color: rgb(135, 152, 63) !important;
          }
        }

        .v-label,
        .single-choice-custom-label {
          width: 100%;
        }

        .single-choice-custom-label {
          font-weight: normal;
          font-size: 14px;

          .v-icon {
            float: right;
          }
        }
      }

      .v-messages {
        font-weight: normal;
      }
    }
  }

  .datasheet-image-record-preview-side {
    p {
      color: rgba(50, 60, 71, 0.7);
      font-size: 15px;

      &.required-asterisk:after {
        content: " *";
        color: #ff5252;
      }
    }

    .theme--light.v-btn.v-btn--disabled {
      background-color: transparent !important;
      text-transform: initial;
    }

    .image-record-wrapper .v-btn ::v-deep {
      padding: 0;

      .v-btn__content {
        height: 36px;
        line-height: 36px;

        .file-uploads {
          width: 100%;
        }
      }
    }
  }

  .organism-record-select-organism-dropdown ::v-deep {
    .v-input__slot {
      background: rgba(255, 255, 255, 0) !important;
      border-color: rgba(0, 0, 0, .5);
      margin-top: 1.5px;
      border-width: thin;
      height: 34px;
      min-height: 24px;
      padding: 0 6px;
      border-bottom: 1px solid rgba(0, 0, 0, .5) !important;

      &:hover {
        border-width: thin !important;
      }

      .v-label {
        color: rgba(0, 0, 0, .8);
        padding-left: 0 !important;
        top: 7px;
      }

      .v-input__append-inner {
        margin-top: 5px;
      }
    }

    .v-input__prepend-outer {
      color: rgba(50, 60, 71, 0.7);
      font-size: 15px;
      min-width: 30%;
      margin-top: 12px;
    }
  }

  .datasheet-image-record-preview-side .image-record-wrapper {
    .v-text-field__details {
      margin-top: 8px;
      padding: 0 12px;

      .invalid-feedback {
        display: block;
        width: 100%;
        margin-top: 0;
        font-size: 100%;
        color: #dc3545;
      }
    }
  }

  .datasheet-view-records {
    top: 10px;
  }

  .datasheet-view-image-record {
    top: 38px;
  }
}

</style>
